'use client';

import { Button as ButtonPrimitive } from '@base-ui-components/react/button';
import type { ComponentPropsWithoutRef, CSSProperties } from 'react';
import { MoonLoader } from 'react-spinners';

import { clsxm } from '@vs-shop/utils';

// The recipe lives in its own server-safe module so server compounds can paint
// a CTA element with it; this client component is one of its consumers, not its
// owner. Re-exporting it from here would hand server callers a client
// reference — `@vs-shop/ui/buttonVariants` is the path that works everywhere.
import { buttonVariants, type ButtonVariantProps } from './buttonVariants';

// Consumer-facing surface is React's loose `ComponentPropsWithoutRef<'button'>`
// (`disabled?: boolean | undefined`, `style?: CSSProperties | undefined`),
// plus the Base UI extras we expose: `render` (polymorphism, replacing Radix
// `asChild`) and `focusableWhenDisabled`. The wrapper coerces those loose
// values into Base UI's strict shape before spreading. `nativeButton` is
// inferred from `render`: passing a `render` prop signals polymorphism
// (`<Link/>`, `<DialogClose/>`, ...) so we flip `nativeButton` to `false` and
// silence Base UI's runtime warning. Consumers wanting the native `<button>`
// just omit `render`.
export type ButtonProps = ComponentPropsWithoutRef<'button'> &
  ButtonVariantProps & {
    render?: ButtonPrimitive.Props['render'];
    focusableWhenDisabled?: boolean;
  };

export const Button = ({
  className,
  variant,
  size,
  disabled,
  loading,
  style,
  render,
  children,
  ...props
}: ButtonProps) => {
  const isDisabled = disabled === true || loading === true;
  const isLoading = loading === true;
  const content = isLoading ? (
    <span className="relative block">
      <span className="invisible">{children}</span>
      <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <MoonLoader size={16} color="currentColor" />
      </span>
    </span>
  ) : (
    children
  );

  // Base UI's `style` and `render` are strict — their optional keys do NOT
  // accept `undefined`. Under `exactOptionalPropertyTypes: true`, spreading
  // possibly-`undefined` values through these keys is a type error, so
  // conditionally spread each only when defined.
  const styleProp: { style: CSSProperties } | object = style === undefined ? {} : { style };
  // When `render` is supplied, the rendered element is not a `<button>` (it's
  // a `<Link>`, `<DialogClose>`, …). Base UI's `nativeButton` defaults to
  // `true`; leaving it true under polymorphic render triggers the runtime
  // warning "A component that acts as a button was not rendered as a native
  // <button>". Pair the two flags atomically.
  const polymorphicProps:
    | { render: NonNullable<ButtonPrimitive.Props['render']>; nativeButton: false }
    | object = render === undefined ? {} : { render, nativeButton: false };

  return (
    <ButtonPrimitive
      data-slot="button"
      className={clsxm(buttonVariants({ variant, size, disabled, loading }), className)}
      disabled={isDisabled}
      // `aria-disabled` is necessary when the rendered element is not a
      // native `<button>` (e.g. `render={<Link />}` → `<a>` — which ignores
      // the HTML `disabled` attribute entirely). Always-set for parity with
      // the legacy Button so screen readers announce disabled state for both
      // the native and polymorphic renderings.
      aria-disabled={isDisabled}
      aria-busy={isLoading ? true : undefined}
      {...styleProp}
      {...polymorphicProps}
      {...props}
    >
      {content}
    </ButtonPrimitive>
  );
};
